<template>
  <div
    :class="[
      'billing-footer w-full py-8 px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative',
      enableButton || isCustom ? 'gradient_background' : 'bg-gray-200 border-t border-gray-300',
    ]"
  >
    <div class="flex flex-row gap-2 justify-between w-full px-28">
      <div v-if="!isCustom" class="flex flex-col justify-center relative">
        <span :class="['text-sm', enableButton || isCustom ? 'text-color-billing' : 'text-gray-600']">Billing Overview</span>
        <span :class="['underline text-[13px] cursor-pointer', enableButton || isCustom ? 'text-white' : 'text-gray-600']" @click="toggleBreakdown" data-breakdown-trigger>See Breakdown</span>
        <div v-if="showBreakdown" ref="breakdownRef" class="absolute left-0 bottom-full bg-white rounded-xl shadow-lg p-4 w-72 z-50">
          <div class="flex justify-between items-center mb-2">
            <div class="font-semibold text-[14px]">Billing Overview</div>
            <button @click="showBreakdown = false" class="text-gray-500 hover:text-black">&times;</button>
          </div>
          <div class="text-[13px] leading-6">
            <div class="flex justify-between">
              <span>{{ userSeats }}× User Seats</span>
              <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></span>
              <span v-else>
                <span class="font-bold">{{ currencySymbol }}{{ selectedPlanId === 'free' ? '0' : totalSeatsPrice }}</span> /{{ getBillingPeriodDisplayText(billingPeriod) }}
              </span>
            </div>
            <div class="flex justify-between">
              <span>Additional Credits</span>
              <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></span>
              <span v-else>
                <span class="font-bold">{{ currencySymbol }}{{ selectedPlanId === 'free' ? '0' : creditPacksAmount }}</span> /{{ getBillingPeriodDisplayText(billingPeriod) }}
              </span>
            </div>
            <div class="flex justify-between">
              <span>Total Credits</span>
              <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></span>
              <span v-else>
                <span class="font-bold text_color">{{ totalCredits }}</span> /{{ getBillingPeriodDisplayText(billingPeriod) }}
              </span>
            </div>
            <div class="flex justify-between border-t pt-2 mt-2">
              <span>Total Billing Amount</span>
              <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></span>
              <span v-else>
                <span class="font-bold text_color">{{ currencySymbol }}{{ selectedPlanId === 'free' ? '0' : billedMonthly }}</span> /{{ getBillingPeriodDisplayText(billingPeriod) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-center mt-4 md:mt-0 ml-auto">
        <div
          v-for="item in summaryItems"
          :key="item.label"
          class="flex flex-col items-center md:items-start mr-8 last:mr-0"
          :class="enableButton || isCustom ? 'text-white' : 'text-gray-900'"
          :style="isCustom ? 'visibility: hidden;' : ''"
        >
          <span class="text-[13px]">{{ item.label }}</span>
          <div class="flex flex-row items-center">
            <div v-if="item.displayValue === 'loading'" class="flex items-center h-[36px]">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 items-center" :class="enableButton || isCustom ? 'border-white' : 'border-gray-700'"></div>
            </div>
            <template v-else>
              <span class="text-[24px] font-bold">{{ item.displayValue }}</span>
              <span class="text-[13px] ml-1">/{{ getBillingPeriodDisplayText(billingPeriod) }}</span>
            </template>
          </div>
        </div>
        <a-button :class="buttonClass" :disabled="shouldDisableButton" :loading="isProcessing || props.loading || props.isProcessingCheckout" @click="handleButtonClick">
          {{ buttonText }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { getBilledLabel, getBillingPeriodDisplayText } from '@/utils/billingPeriodUtils';

const props = defineProps({
  billedMonthly: {
    type: [String, Number],
    required: true,
  },
  dueToday: {
    type: [String, Number],
    required: true,
  },
  userSeats: {
    type: Number,
    default: 1,
  },
  creditPacks: {
    type: Number,
    default: 0,
  },
  billingPeriod: {
    type: String,
    default: 'month',
  },
  userSeatsAmount: {
    type: Number,
    default: 0,
  },
  creditPacksAmount: {
    type: Number,
    default: 0,
  },
  totalCredits: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  enableButton: {
    type: Boolean,
    default: true,
  },
  totalSeatsPrice: {
    type: Number,
    default: 0,
  },
  currentPlanId: {
    type: String,
    default: 'free',
  },
  selectedPlanId: {
    type: String,
    default: 'free',
  },
  selectedCreditPack: Object,
  isProcessingCheckout: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  trialDays: {
    type: Number,
    default: 0,
  },
  currencySymbol: {
    type: String,
    default: '$',
  },
});

const emit = defineEmits(['setup-billing', 'see-breakdown', 'cancel-trial']);
const showDowngradeModal = ref(false);
const downgradeResolve = ref(null);
const showBreakdown = ref(false);
const isProcessing = ref(false);
const breakdownRef = ref(null);

const isCustom = computed(() => {
  const isCustomCreditPack = props.selectedCreditPack && props.selectedCreditPack.isCustom;
  const isDowngrading = (props.currentPlanId === 'growth' || props.currentPlanId === 'starter') && props.selectedPlanId === 'free' && props.status !== 'FREE_TRIAL';
  const isFreeTrialDowngrading = props.status === 'FREE_TRIAL' && props.selectedPlanId === 'free';
  return isCustomCreditPack || isDowngrading || isFreeTrialDowngrading;
});

const shouldDisableButton = computed(() => {
  // Talk to Sales is always enabled
  if (isCustom.value) return false;
  // Otherwise use normal logic - disable during loading, checkout processing, or when button is disabled
  // isProcessingCheckout keeps button disabled until page refresh after successful checkout
  return !props.enableButton || props.loading || props.isProcessingCheckout;
});

const summaryItems = computed(() => [
  {
    label: getBilledLabel(props.billingPeriod),
    value: props.loading ? 'loading' : props.billedMonthly,
    displayValue: props.loading ? 'loading' : props.currencySymbol + (props.selectedPlanId === 'free' ? '0' : props.billedMonthly),
  },
  {
    label: 'Due Today',
    value: props.loading ? 'loading' : props.dueToday,
    displayValue: props.loading ? 'loading' : props.currencySymbol + (props.selectedPlanId === 'free' ? '0' : props.dueToday),
  },
]);

const handleButtonClick = async () => {
  if (isProcessing.value) return;

  isProcessing.value = true;

  try {
    if (props.selectedCreditPack && props.selectedCreditPack.isCustom) {
      window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
      return;
    }

    // For FREE_TRIAL users selecting free plan, emit cancel trial event
    if (props.status === 'FREE_TRIAL' && props.selectedPlanId === 'free') {
      emit('cancel-trial');
      return;
    }

    // For paid users downgrading to free, open Talk to Sales
    const isDowngrading = (props.currentPlanId === 'growth' || props.currentPlanId === 'starter') && props.selectedPlanId === 'free' && props.status !== 'FREE_TRIAL';
    if (isDowngrading) {
      window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
      return;
    }

    emit('setup-billing');
  } finally {
    setTimeout(() => {
      isProcessing.value = false;
    }, 500);
  }
};

const buttonText = computed(() => {
  if (props.selectedCreditPack && props.selectedCreditPack.isCustom) {
    return 'Talk to Sales';
  }

  if (props.currentPlanId === 'free' && (props.selectedPlanId === 'growth' || props.selectedPlanId === 'starter')) {
    return 'Upgrade Now';
  }

  // For FREE_TRIAL users selecting free plan, show Cancel Subscription
  if (props.status === 'FREE_TRIAL' && props.selectedPlanId === 'free') {
    return 'Cancel Trial';
  }

  // For paid users downgrading to free, show Talk To Sales
  if ((props.currentPlanId === 'growth' || props.currentPlanId === 'starter') && props.selectedPlanId === 'free' && props.status !== 'FREE_TRIAL') {
    return 'Talk To Sales';
  }

  if (props.currentPlanId === 'growth' && props.selectedPlanId === 'growth') {
    return 'Upgrade Now';
  }

  if (props.currentPlanId === 'starter' && props.selectedPlanId === 'starter') {
    return 'Upgrade Now';
  }

  if (props.currentPlanId === 'growth' && props.selectedPlanId === 'starter' && props.billingPeriod === 'year') {
    return 'Upgrade Now';
  }

  if (props.currentPlanId === 'growth' && props.selectedPlanId === 'starter') {
    return 'Downgrade Now';
  }

  // Plan upgrades/downgrades between starter and growth
  if ((props.currentPlanId === 'starter' && props.selectedPlanId === 'growth') || (props.currentPlanId === 'growth' && props.selectedPlanId === 'starter')) {
    return 'Upgrade Now';
  }

  return 'Upgrade Now';
});

const buttonClass = computed(() => {
  // If enabled (including Talk to Sales), use gradient style
  if (props.enableButton || isCustom.value) {
    return 'primary_medium_dark font-semibold px-8';
  }
  return 'primary_medium font-semibold';
});

const toggleBreakdown = async () => {
  showBreakdown.value = !showBreakdown.value;
  await nextTick();
  emit('see-breakdown', showBreakdown.value);
};

const handleClickOutside = (event) => {
  // Don't close if clicking on the "See Breakdown" link itself
  if (event.target.closest('[data-breakdown-trigger]')) {
    return;
  }

  if (showBreakdown.value && breakdownRef.value && !breakdownRef.value.contains(event.target)) {
    showBreakdown.value = false;
    emit('see-breakdown', false);
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.billing-footer {
  height: 90px;
}
.billing_button {
  transition: box-shadow 0.2s;
}
.billing_button:hover {
  box-shadow: 0 4px 16px rgba(112, 149, 235, 0.18);
}

.text-color-billing {
  color: rgba(255, 255, 255, 0.6);
}
</style>
